import React, { useMemo } from 'react';
import { Alert, Box, Tab, Tabs, Typography } from '@mui/material';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import WatchTutorialLink from '../help/WatchTutorialLink';

const BASE_TABS = [
  { label: 'Overview', path: '/call-qa/overview', permission: 'call_qa.read' },
  { label: 'Calls', path: '/call-qa/calls', permission: 'call_qa.calls.read' },
  { label: 'Investigations', path: '/call-qa/investigations', permission: 'call_qa.investigations.read' },
  { label: 'Scorecards', path: '/call-qa/scorecards', permission: 'call_qa.scorecards.read' },
  { label: 'Services', path: '/call-qa/services', permission: 'call_qa.scorecards.read' },
  { label: 'Agents', path: '/call-qa/agents', permission: 'call_qa.scoring.read' },
  { label: 'Conduct', path: '/call-qa/conduct', permission: 'call_qa.calls.read' },
  { label: 'Campaigns', path: '/call-qa/campaigns', permission: 'call_qa.scorecards.read' },
  { label: 'Coaching', path: '/call-qa/coaching', permission: 'call_qa.coaching.read' },
  { label: 'Reports', path: '/call-qa/reports', permission: 'call_qa.read' },
  { label: 'Configuration', path: '/call-qa/configuration', permission: 'call_qa.configure' },
];

const CallQaLayout: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const permissions = user?.permissions || [];
  const canAccess = permissions.includes('call_qa.read')
    || permissions.includes('call_qa.calls.read')
    || permissions.includes('call_qa.configure');

  const tabs = useMemo(
    () => BASE_TABS.filter((tab) => !tab.permission || permissions.includes(tab.permission)),
    [permissions],
  );

  if (!canAccess) {
    return (
      <Box sx={{ mt: 2 }}>
        <Alert severity="warning">You do not have permission to view Call QA.</Alert>
      </Box>
    );
  }

  const currentTab = tabs.find((tab) => location.pathname.startsWith(tab.path))?.path
    || (location.pathname.includes('/calls/') ? '/call-qa/calls' : '/call-qa/overview');

  return (
    <Box sx={{ width: '100%' }}>
      <Box sx={{ mb: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Typography variant="h4" gutterBottom>Call QA</Typography>
          <WatchTutorialLink contextKey="call-qa" />
        </Box>
        <Typography variant="body2" color="text.secondary">
          Provider-independent call quality review — recordings, transcripts, and evidence-based scoring.
        </Typography>
      </Box>
      <Tabs
        value={currentTab}
        onChange={(_, value) => navigate(value)}
        variant="scrollable"
        scrollButtons="auto"
        sx={{ mb: 2, borderBottom: 1, borderColor: 'divider' }}
      >
        {tabs.map((tab) => (
          <Tab key={tab.path} label={tab.label} value={tab.path} />
        ))}
      </Tabs>
      <Outlet />
    </Box>
  );
};

export default CallQaLayout;
